import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  LayoutDashboard, Network, StickyNote, ShieldCheck,
  Landmark, Triangle, HelpCircle, Swords, Map, Newspaper, Mic, FileSearch, Receipt,
  MapPin, ChevronDown, Handshake, SlidersHorizontal,
} from 'lucide-react';
import { NavCustomizer } from '@techstack/ui';
import { applyNavPrefs } from '@techstack/core';
import '@techstack/ui/dist/styles/nav-customizer.css';
import { useAccountTabPrefs, hasAccountTabPrefsAccount } from '../../hooks/useAccountTabPrefs';
import useModalBehavior from '../../hooks/useModalBehavior';

/*
 * AccountsTabs — horizontal tab strip for the per-account dashboard on
 * /accounts. Mirrors the GAS AccountDashboard tab anatomy (App.jsx
 * ALL_TABS); hash-synced (#tab=<key>) using the same pattern as
 * VendorCompetitiveAnalysis/VendorTabs.js so links and reload preserve
 * the active tab.
 */

/*
 * THE SHIPPED ORDER. Dash, People, Partners lead (Ryan, 2026-09-18) — the
 * three boards, and the three questions asked first about an account: how is
 * it doing, who is in it, who carries us into it. Everything after them is the
 * deep view behind some widget.
 *
 * This is a DEFAULT, not the order anyone sees: a rep who has arranged their
 * own strip keeps it, and `sortByNavPrefs` appends anything new — including a
 * tab added after they customized — at the end rather than dropping it.
 */
export const ACCOUNT_TABS = [
  { key: 'overview', label: 'Dash', icon: LayoutDashboard },
  // One People tab, GAS-style: the org chart and the contact list are two
  // views of the same roster, not two tabs (Ryan, 2026-09-09).
  { key: 'org', label: 'People', icon: Network },
  // Who carries us into this account, and on what paper. A board like the Dash
  // and People — see ACCOUNT_BOARDS in @techstack/core.
  { key: 'partners', label: 'Partners', icon: Handshake },
  { key: 'notes', label: 'Notes', icon: StickyNote },
  { key: 'news', label: 'News', icon: Newspaper },
  { key: 'meetings', label: 'Meeting notes', icon: Mic },
  // The full view behind the money widgets. The shared catalog has pointed
  // four of them at `deepTab: 'procurement'` since the board shipped; GAS had
  // the tab and TekStak did not, so all four silently lost their arrow.
  { key: 'procurement', label: 'Procurement', icon: Receipt },
  { key: 'zt', label: 'Zero trust', icon: ShieldCheck },
  { key: 'budget', label: "BCP's", icon: Landmark },
  { key: 'pyramid', label: 'Value pyramid', icon: Triangle },
  { key: 'traps', label: 'Trap questions', icon: HelpCircle },
  { key: 'competitive', label: 'Competitive', icon: Swords },
  { key: 'solicitations', label: 'Solicitations', icon: FileSearch },
  { key: 'techmap', label: 'Tech map', icon: Map },
  // Where the account physically is. Sits next to Tech map on purpose: the
  // Tech map is the LOGICAL estate, Locations the physical one, and
  // agency_site_tech links them rather than merging them.
  { key: 'locations', label: 'Locations', icon: MapPin },
];

// The CAP on what the strip will show; how many actually fit is measured at
// runtime (see AccountsTabs). Fourteen tabs scrolled at laptop widths and the
// ones past the edge were, in effect, hidden anyway (Ryan, 2026-09-13) —
// better hidden on purpose, with a menu that names them. The cap stays
// because a very wide screen showing all fourteen is a worse strip than one
// that keeps a short, scannable row. A folded tab that is ACTIVE takes the
// More button's label, so the strip always says where you are.
const VISIBLE_TABS = 9;
// The cap is against the tabs the rep actually KEPT, not the shipped catalogue:
// switching six tabs off should shorten the strip, not leave the cap pinned at
// a number the list can no longer reach.
const capFor = (tabs) => Math.min(tabs.length, VISIBLE_TABS);

const HASH_PREFIX = '#tab=';

function readHash() {
  if (typeof window === 'undefined') return ACCOUNT_TABS[0].key;
  const h = window.location.hash;
  if (!h.startsWith(HASH_PREFIX)) return ACCOUNT_TABS[0].key;
  const candidate = h.slice(HASH_PREFIX.length);
  return ACCOUNT_TABS.find(t => t.key === candidate) ? candidate : ACCOUNT_TABS[0].key;
}

export function useAccountTabs() {
  const [activeKey, setActiveKey] = useState(readHash);

  // Listen for hash changes (back/forward navigation, link clicks).
  useEffect(() => {
    const onHash = () => setActiveKey(readHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const setTab = (key) => {
    if (!ACCOUNT_TABS.find(t => t.key === key)) return;
    setActiveKey(key);
    // replaceState rather than location.hash so tab clicks don't
    // pollute the history stack (same rationale as VendorTabs).
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.hash = `${HASH_PREFIX}${key}`;
      window.history.replaceState({}, '', url);
    }
  };

  return [activeKey, setTab];
}

/*
 * Dash is LOCKED — reorderable, never hideable. `useAccountTabs` falls back to
 * ACCOUNT_TABS[0] for an absent or unknown hash, so if Dash could be switched
 * off the fallback would land on a tab that renders nothing. Keeping one
 * destination always reachable is the same rule Settings follows in the
 * sidebar customizer.
 */
const LOCKED_TAB_KEYS = new Set(['overview']);

/** The reorder panel, in a dialog so the strip it edits stays visible behind it. */
function TabOrderModal({ onClose }) {
  const { prefs, setPrefs, saving, error } = useAccountTabPrefs();
  const modal = useModalBehavior(onClose);

  const items = ACCOUNT_TABS.map((tab) => {
    const Icon = tab.icon;
    return {
      key: tab.key,
      label: tab.label,
      icon: <Icon size={16} />,
      locked: LOCKED_TAB_KEYS.has(tab.key),
    };
  });

  const status = error
    ? `Not saved to your account: ${error}`
    : saving
      ? 'Saving…'
      : hasAccountTabPrefsAccount()
        ? 'Saved automatically'
        : 'Saved on this device only';

  return (
    <div className="accounts-taborder-overlay" {...modal.overlayProps}>
      {/* role and aria-modal come from dialogProps — it owns them. */}
      <div className="accounts-taborder-modal" aria-label="Arrange account tabs" {...modal.dialogProps}>
        <NavCustomizer
          items={items}
          prefs={prefs}
          onChange={setPrefs}
          busy={saving}
          status={status}
          title="Account tabs"
          // Or every toggle tells a screen-reader user their TAB is being
          // shown "in the sidebar".
          surfaceLabel="tab strip"
          subtitle={
            <>
              Drag a row (or use the arrows) to set the order these tabs sit in
              on every account. Switch one off to take it out of the strip — it
              stays listed here, and its link keeps working if you have it
              bookmarked.
            </>
          }
        />
        <div className="accounts-taborder-foot">
          <button type="button" className="accounts-taborder-done" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

export function AccountsTabs({ activeKey, onChange, trailing = null }) {
  /*
   * `trailing` and the More menu sit OUTSIDE the scrolling strip: the strip
   * is overflow-x auto, which also clips vertically, so a menu inside it
   * could never open, and a More button inside it would scroll away at the
   * widths where it matters most. The bar owns the bottom rule.
   */
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  useEffect(() => {
    if (!moreOpen) return undefined;
    const close = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [moreOpen]);

  /*
   * How many tabs fit is a measurement, not a constant. A fixed nine was
   * right at one width and wrong at every other: at 1440 the ninth tab
   * ("Procurement") ran under the More button and rendered as "Procuren",
   * a word cut mid-letter, because the strip scrolls rather than wraps.
   *
   * So the widths are measured once — the labels never change, so once is
   * enough — and the count is recomputed whenever the bar resizes. The first
   * paint renders every tab, which is what makes that measurement possible;
   * it is also the honest fallback if measurement never runs, since the strip
   * still scrolls rather than hiding anything.
   */
  /*
   * The rep's own order and on/off switches. `applyNavPrefs` orders then
   * SUBTRACTS — it can never introduce a tab the catalogue does not carry —
   * and a tab with no saved slot keeps its shipped position at the end, so a
   * tab added next month appears for someone who customized last month.
   */
  const { prefs: tabPrefs } = useAccountTabPrefs();
  const tabs = useMemo(
    () => applyNavPrefs(ACCOUNT_TABS, tabPrefs, (t) => t.key),
    [tabPrefs],
  );
  const cap = capFor(tabs);

  const barRef = useRef(null);
  const stripRef = useRef(null);
  const tabWidthsRef = useRef(null);
  const moreWidthRef = useRef(0);
  const [visibleCount, setVisibleCount] = useState(cap);
  const [orderOpen, setOrderOpen] = useState(false);

  /*
   * The measurement cache is POSITIONAL — `widths[i]` is the width of slot i —
   * so it is only valid for the order it was taken in. Reordering "Dash,
   * People, Partners" to "Partners, Dash, People" keeps three entries and
   * changes which label each one describes; without this signature the strip
   * would fold using the previous order's widths and cut a label mid-word,
   * which is the exact bug the measurement exists to prevent.
   */
  const sig = tabs.map((t) => t.key).join('|');

  const recount = useCallback(() => {
    const bar = barRef.current;
    const strip = stripRef.current;
    const cache = tabWidthsRef.current;
    const widths = cache && cache.sig === sig ? cache.widths : null;
    if (!bar || !strip || !widths || !widths.length) return;
    const gap = parseFloat(getComputedStyle(strip).columnGap || '0') || 0;
    const barGap = parseFloat(getComputedStyle(bar).columnGap || '0') || 0;
    const trailingEl = bar.querySelector(':scope > .accounts-tabs__trailing');
    const trailingW = trailingEl ? trailingEl.getBoundingClientRect().width + barGap : 0;
    // The More button is reserved whenever the catalog outruns the cap, which
    // it always does today — so it is paid for up front rather than discovered
    // after the strip has already overflowed.
    // The More button is always reserved: it now carries "Arrange tabs…" even
    // when nothing is folded, so the strip must leave room for it whatever the
    // catalogue length.
    const moreW = (moreWidthRef.current || 96) + barGap;
    const room = bar.clientWidth - trailingW - moreW;

    let n = 0;
    let acc = 0;
    for (let i = 0; i < widths.length; i += 1) {
      const next = acc + (i ? gap : 0) + widths[i];
      if (next > room) break;
      acc = next;
      n = i + 1;
    }
    setVisibleCount(Math.max(1, n));
  }, [sig]);

  // A new order invalidates the cache and re-opens the strip to the full cap,
  // which is what makes the next layout pass able to measure it again.
  useLayoutEffect(() => {
    if (tabWidthsRef.current && tabWidthsRef.current.sig !== sig) {
      tabWidthsRef.current = null;
      setVisibleCount(cap);
    }
  }, [sig, cap]);

  useLayoutEffect(() => {
    const strip = stripRef.current;
    if (strip && !tabWidthsRef.current) {
      const els = Array.from(strip.children);
      // The strip never renders more than the cap, so the cap is what gets
      // measured — waiting for all fourteen was the bug in the first cut of
      // this, and it left the count permanently at its initial value. Only
      // trust a real layout: a strip measured at zero width (hidden panel,
      // first paint in some browsers) would otherwise fold everything away.
      if (els.length === cap && els.every((el) => el.getBoundingClientRect().width > 0)) {
        tabWidthsRef.current = { sig, widths: els.map((el) => el.getBoundingClientRect().width) };
      }
    }
    const more = moreRef.current;
    if (more) moreWidthRef.current = Math.max(moreWidthRef.current, more.getBoundingClientRect().width);
    recount();
  });

  useEffect(() => {
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => recount());
    ro.observe(bar);
    return () => ro.disconnect();
  }, [recount]);

  const fit = Math.min(visibleCount, cap);
  const shown = tabs.slice(0, fit);
  const folded = tabs.slice(fit);
  const activeFolded = folded.find((t) => t.key === activeKey);
  const MoreIcon = activeFolded ? activeFolded.icon : null;

  const renderTab = (tab) => {
    const Icon = tab.icon;
    const isActive = tab.key === activeKey;
    return (
      <button
        key={tab.key}
        role="tab"
        aria-selected={isActive}
        className={`accounts-tab ${isActive ? 'is-active' : ''}`}
        onClick={() => { onChange(tab.key); setMoreOpen(false); }}
      >
        <Icon size={14} />
        <span>{tab.label}</span>
      </button>
    );
  };

  return (
    <div className="accounts-tabbar" ref={barRef}>
    <div className="accounts-tabs" role="tablist" aria-label="Account sections" ref={stripRef}>
      {shown.map(renderTab)}
    </div>
    {/* The menu renders even with nothing folded — it is the only route to
        "Arrange tabs…", and a rep on a wide screen showing every tab is
        exactly the one most likely to want a different order. */}
    <div className="accounts-tabs__more" ref={moreRef}>
      <button
        type="button"
        className={`accounts-tab accounts-tab--more ${activeFolded ? 'is-active' : ''} ${moreOpen ? 'is-open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={moreOpen}
        onClick={() => setMoreOpen((v) => !v)}
      >
        {MoreIcon ? <MoreIcon size={14} /> : null}
        <span>{activeFolded ? activeFolded.label : 'More'}</span>
        <ChevronDown size={12} className="accounts-tab__chevron" />
      </button>
      {moreOpen && (
        <div className="accounts-tabs__menu" role="menu">
          {folded.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.key === activeKey;
            return (
              <button
                key={tab.key}
                role="menuitemradio"
                aria-checked={isActive}
                className={`accounts-tabs__item ${isActive ? 'is-active' : ''}`}
                onClick={() => { onChange(tab.key); setMoreOpen(false); }}
              >
                <Icon size={14} />
                <span>{tab.label}</span>
              </button>
            );
          })}
          {folded.length > 0 && <div className="accounts-tabs__sep" role="separator" />}
          <button
            type="button"
            role="menuitem"
            className="accounts-tabs__item accounts-tabs__item--action"
            onClick={() => { setMoreOpen(false); setOrderOpen(true); }}
          >
            <SlidersHorizontal size={14} />
            <span>Arrange tabs…</span>
          </button>
        </div>
      )}
    </div>
    {trailing != null && <div className="accounts-tabs__trailing">{trailing}</div>}
    {orderOpen && <TabOrderModal onClose={() => setOrderOpen(false)} />}
    </div>
  );
}

export default AccountsTabs;
